import { Flame, Calendar, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface StreakDisplayProps {
  currentStreak: number;
  longestStreak: number;
  totalWorkouts: number;
  compact?: boolean;
  className?: string;
}

export function StreakDisplay({
  currentStreak,
  longestStreak,
  totalWorkouts,
  compact = false,
  className
}: StreakDisplayProps) {
  const isOnFire = currentStreak >= 3;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className={cn(
          "flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium",
          isOnFire 
            ? "bg-warning/20 text-warning"
            : "bg-muted text-muted-foreground"
        )}>
          <Flame className={cn("h-4 w-4", isOnFire && "animate-pulse")} />
          <span>{currentStreak} day streak</span>
        </div>
      </div>
    );
  }

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-4">
        <div className="grid grid-cols-3 gap-4">
          {/* Current Streak */}
          <div className="text-center">
            <div className={cn(
              "inline-flex items-center justify-center w-12 h-12 rounded-full mb-2",
              isOnFire 
                ? "bg-gradient-to-br from-warning to-destructive text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}>
              <Flame className={cn("h-6 w-6", isOnFire && "animate-pulse")} />
            </div>
            <p className="text-2xl font-bold">{currentStreak}</p>
            <p className="text-xs text-muted-foreground">Day Streak</p>
          </div>

          {/* Longest Streak */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-warning/20 text-warning mb-2">
              <Trophy className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold">{longestStreak}</p>
            <p className="text-xs text-muted-foreground">Best Streak</p>
          </div>

          {/* Total Workouts */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/20 text-primary mb-2">
              <Calendar className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold">{totalWorkouts}</p>
            <p className="text-xs text-muted-foreground">Total Workouts</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
